import React, { forwardRef, useState } from "react";
import { Event } from "../../types/index";
import { Dot, Lock } from "../../../../icons";
import { EventLocation, Calender } from "../../../../icons/tags-icons";
import moment from "moment";
import { getLocalStorage } from "../../../../helpers/storage";
import { toastError } from "../../../toast";
interface EventCardProps {
  event: Event;
  onClick: (event: Event, isMyEvent: boolean, isMemberOnly?: boolean) => void;
  isMyEvent: boolean;
}

const SkeletonText = ({ width = "w-48", height = "h-4" }) => (
  <div
    className={`bg-gray-200 rounded-full dark:bg-gray-700 ${width} ${height} mb-4`}
  />
);

const SkeletonCard = () => (
  <div className="flex flex-col bg-black border border-[#404040] shadow rounded-[10px] overflow-hidden">
    <div className="h-[196px] w-full bg-gray-700"></div>
    <div className="p-4 h-full">
      <div className="flex items-center flow-root">
        <SkeletonText width="w-32" height="h-6" />
        <div className="flex gap-2 float-right">
          <SkeletonText width="w-16" height="h-4" />
          <SkeletonText width="w-20" height="h-4" />
        </div>
      </div>
      <div className="mt-3 flex flex-col">
        <div className="flex">
          <SkeletonText width="w-8" height="h-4" />
          <SkeletonText width="w-24" height="h-4" />
        </div>
        <div className="flex mt-2">
          <SkeletonText width="w-8" height="h-4" />
          <SkeletonText width="w-24" height="h-4" />
        </div>
      </div>
      <div className="mt-4 flex flex-row items-center">
        <div className="flex -space-x-4 rtl:space-x-reverse">
          <SkeletonText width="w-8" height="h-8 rounded-full" />
          <SkeletonText width="w-8" height="h-8 rounded-full" />
          <SkeletonText width="w-8" height="h-8 rounded-full" />
          <SkeletonText width="w-8" height="h-8 rounded-full" />
        </div>
        <div className="ml-auto">
          <SkeletonText width="w-24" height="h-8 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

const EventCard = forwardRef<HTMLDivElement, EventCardProps>(
  ({ event, onClick, isMyEvent }, ref) => {
    const user = JSON.parse(getLocalStorage("userData") || "{}");
    const [isBannerLoading, setIsBannerLoading] = useState(true);

    const onClickHandler = () => {
      onClick(
        event,
        isMyEvent || false,
        (event.member_only && (!user?.memberships?.is_opc || false)) || false,
      );
    };

    const handleImageLoad = () => {
      setIsBannerLoading(false);
    };

    const handleImageError = () => {
      setIsBannerLoading(false);
    };

    const handleJoinNow = () => {
      if (!isMyEvent) {
        onClickHandler();
      } else {
        if (event.zoom_join_url) {
          window.location.href = event.zoom_join_url;
        } else {
          toastError({ message: "No join URL found for this event." });
        }
      }
    };

    return (
      <div
        ref={ref}
        className={`relative flex flex-col bg-black border border-[#404040] shadow rounded-[10px] overflow-hidden w-[320px] min-h-[400px] max-h-[420px] ${
          moment(event.end_time).isBefore(moment()) ? "grayscale" : ""
        }`}
        onClick={() => {
          if (
            event.event_type === "ONLINE" &&
            new Date(event.start_time).getTime() - new Date().getTime() <
              10 * 60 * 1000
          ) {
            handleJoinNow();
          } else {
            onClickHandler();
          }
        }}
      >
        {isBannerLoading && <SkeletonCard />}
        <div className="relative">
          <img
            src={event.banner}
            alt={`Event - ${event.title}`}
            className={`w-full object-cover ${isBannerLoading ? "hidden" : "block"}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </div>
        {!isBannerLoading && (
          <div className="p-4 h-full">
            <div className="flex flex-row items-center flow-root">
              <div
                className="text-white font-bold text-lg float-left w-[70%] overflow-hidden"
                style={{
                  maxHeight: "4.5em",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {event.title}
              </div>
              <div className="float-right flex flex-wrap gap-2 justify-center items-center w-[30%]">
                <div className="flex justify-center items-center rounded-[22px] px-[8px] py-[6px] bg-[#404040]">
                  <div>
                    <Dot
                      width={8}
                      height={8}
                      fillColor={`${
                        event.event_type === "ONLINE" ? "#6EE755" : "#ECD996"
                      }`}
                    />
                  </div>
                  <div className="ml-[4px] text-xs text-white">
                    {event.event_type === "ONLINE" ? "ONLINE" : "OFFLINE"}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-col mb-10">
              <div className="flex">
                <Calender width={16} height={16} />
                <div className="ml-[6px]">
                  <div className="text-white text-xs font-normal">
                    {moment(event.start_time).format("D MMM YYYY, ddd, h:mm a")}
                  </div>
                </div>
              </div>
              {event?.location && (
                <div className="flex mt-[6px]">
                  <EventLocation width={16} height={16} />
                  <div className="ml-[6px]">
                    <div className="text-white text-xs font-normal">
                      {event.location}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={`mt-4 flex flex-row items-center flow-root`}>
              {event.meta?.profile_pics &&
                event.meta?.profile_pics?.length > 2 && (
                  <div className="absolute bottom-[14px]">
                    <div
                      className={`flex -space-x-4 rtl:space-x-reverse float-left ${
                        event.total_tickets_sold === 0 && "hidden"
                      }`}
                    >
                      {/* Profile images */}
                      <img
                        className="w-8 h-8 border-2 border-[#262626] rounded-full"
                        src={event?.meta?.profile_pics?.[0]}
                        alt="Profile"
                      />
                      <img
                        className="w-8 h-8 border-2 border-[#262626] rounded-full"
                        src={event?.meta?.profile_pics?.[1]}
                        alt="Profile"
                      />
                      <img
                        className="w-8 h-8 border-2 border-[#262626] rounded-full"
                        src={event?.meta?.profile_pics?.[2]}
                        alt="Profile"
                      />
                      <div className="flex items-center justify-center w-8 h-8 text-[9px] font-medium text-white bg-[#404040] border-2 border-[#262626] rounded-full">
                        <span>+{event.total_tickets_sold}</span>
                      </div>
                    </div>
                  </div>
                )}
              <div
                className={`absolute bottom-[14px] right-[24px] px-4 py-[6px] rounded-[100px] cursor-pointer filter-none !grayscale-0 float-right ${
                  isMyEvent && "hidden"
                }
                ${event.member_only && !user?.memberships?.is_opc ? "bg-[#404040]" : "bg-[#007AFF]"}
                ${event.sold_out ? "bg-[#404040]" : "bg-[#007AFF]"} font-semibold text-white text-[13px] 
                ${event.member_only && !user?.memberships?.is_opc && " text-gold-gradient "}
                `}
              >
                {moment(event.end_time).isBefore(moment()) ? (
                  "Know More"
                ) : event.sold_out ? (
                  "Sold Out"
                ) : event.member_only && !user?.memberships?.is_opc ? (
                  <div className="flex flex-row gap-2 items-center">
                    <Lock />
                    <span className="bg-clip-text text-transparent bg-gold-gradient">
                      Members only
                    </span>
                  </div>
                ) : new Date(event.start_time).getTime() -
                    new Date().getTime() <
                    10 * 60 * 1000 && event.event_type === "ONLINE" ? (
                  "Join Webinar"
                ) : (
                  "Register"
                )}
              </div>
              {isMyEvent && (
                <div
                  className={`absolute bottom-[16px] right-[20px] py-[6px] rounded-[100px] cursor-pointer text-[#007AFF] font-bold float-right`}
                >
                  {new Date(event.start_time).getTime() - new Date().getTime() <
                    10 * 60 * 1000 && event.event_type === "ONLINE"
                    ? "Join Now"
                    : "View ticket"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default EventCard;
